import { Icon } from "@iconify/react/dist/iconify.js";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	useNavigate,
	useRouter,
} from "@tanstack/react-router";
import { type FormEvent, useId, useState, useTransition } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import {
	deleteUserAccount,
	logoutUser,
	updateUserName,
	updateUserPassword,
} from "$/modules/auth/lib/auth";
import { currentUserQueryOptions } from "$/modules/auth/lib/queries";
import { PasswordInput } from "$/modules/auth/ui/password-input";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "$/modules/shared/ui/alert-dialog";
import { Button } from "$/modules/shared/ui/button";
import { Card, CardContent, CardHeader } from "$/modules/shared/ui/card";
import { ConstructionHeader } from "$/modules/shared/ui/construction-header";
import { Field, FieldLabel } from "$/modules/shared/ui/field";
import { Input } from "$/modules/shared/ui/input";
import { PageContainer } from "$/modules/shared/ui/page-container";
import { Spinner } from "$/modules/shared/ui/spinner";

export const Route = createFileRoute("/$locale/$userId/profile/me/")({
	component: ProfilePage,
	loader: async ({ context: { queryClient } }) => {
		await queryClient.ensureQueryData(currentUserQueryOptions());
	},
});

function ProfilePage() {
	const fieldId = useId();

	const t = useTranslations();
	const { data: user } = useSuspenseQuery(currentUserQueryOptions());
	const { locale } = Route.useParams();
	const navigate = useNavigate();
	const router = useRouter();

	const [name, setName] = useState(user?.name ?? "");
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [deleteConfirmation, setDeleteConfirmation] = useState("");
	const [isNamePending, startName] = useTransition();
	const [isPasswordPending, startPassword] = useTransition();
	const [isDeletePending, startDelete] = useTransition();
	const [isDeleteModalOpen, onDeleteModalOpenChange] = useState(false);
	const onDeleteModalOpen = () => onDeleteModalOpenChange(true);
	const onDeleteModalClose = () => onDeleteModalOpenChange(false);

	if (!user) {
		void navigate({ to: "/$locale/signin", params: { locale } });
		return null;
	}

	const notify = (message: string, ok: boolean) =>
		toast[
			(ok ? "success" : "danger") === "danger"
				? "error"
				: (ok ? "success" : "danger") === "success"
					? "success"
					: "info"
		](message, {});

	const saveName = (e: FormEvent) => {
		e.preventDefault();
		startName(async () => {
			const res = await updateUserName(name);
			notify(res.success ? t("profile.nameSaved") : res.message, res.success);
		});
	};

	const savePassword = (e: FormEvent) => {
		e.preventDefault();
		startPassword(async () => {
			const res = await updateUserPassword(newPassword, currentPassword);
			if (res.success) {
				setCurrentPassword("");
				setNewPassword("");
			}
			notify(
				res.success ? t("profile.passwordSaved") : res.message,
				res.success,
			);
		});
	};

	const signOut = async () => {
		await logoutUser();
		router.clearCache();
		void navigate({ to: "/" });
	};

	const deleteAccount = () => {
		startDelete(async () => {
			const res = await deleteUserAccount();
			if (!res.success) {
				notify(res.message, false);
				return;
			}
			router.clearCache();
			void navigate({ to: "/" });
		});
	};

	return (
		<AlertDialog
			onOpenChange={(open) => {
				if (open || !isDeletePending) onDeleteModalOpenChange(open);
			}}
			open={isDeleteModalOpen}
		>
			<PageContainer>
				<ConstructionHeader
					titleKey="profile.title"
					description={{ hasKey: true, descriptionKey: "profile.description" }}
				/>
				<div className="mb-6 flex flex-wrap gap-2">
					<Button asChild variant="default">
						<Link
							to="/$locale/$userId/profile/me"
							params={{ locale, userId: user.$id }}
						>
							{t("profile.navigation.profile")}
						</Link>
					</Button>
					<Button asChild variant="muted">
						<Link
							to="/$locale/$userId/profile/settings"
							params={{ locale, userId: user.$id }}
						>
							{t("profile.navigation.settings")}
						</Link>
					</Button>
				</div>

				<div className="grid gap-6 md:grid-cols-2">
					<Card className="border border-default-200/80">
						<CardHeader className="flex items-center gap-2 px-6 pt-6 font-semibold">
							<Icon icon="lucide:user" className="size-5 text-primary" />
							{t("profile.account")}
						</CardHeader>
						<CardContent className="gap-4 px-6 pb-6">
							<form onSubmit={saveName} className="space-y-4">
								<Field>
									<FieldLabel htmlFor={`${fieldId}-100`}>
										{t("auth.name")}
									</FieldLabel>
									<Input
										id={`${fieldId}-100`}
										value={name}
										minLength={2}
										required
										onChange={(event) => setName(event.currentTarget.value)}
									/>
								</Field>
								<Field data-disabled>
									<FieldLabel htmlFor={`${fieldId}-101`}>
										{t("email")}
									</FieldLabel>
									<Input
										id={`${fieldId}-101`}
										value={user.email}
										readOnly
										disabled
									/>
								</Field>
								<Button
									type="submit"
									disabled={isNamePending || name === user.name}
									variant="default"
								>
									{isNamePending && <Spinner data-icon="inline-start" />}
									{t("profile.saveName")}
								</Button>
							</form>
						</CardContent>
					</Card>

					<Card className="border border-default-200/80">
						<CardHeader className="flex items-center gap-2 px-6 pt-6 font-semibold">
							<Icon icon="lucide:lock" className="size-5 text-primary" />
							{t("profile.security")}
						</CardHeader>
						<CardContent className="gap-4 px-6 pb-6">
							<form onSubmit={savePassword} className="space-y-4">
								<PasswordInput
									autoComplete="current-password"
									label={t("profile.currentPassword")}
									placeholder={t("passwordPlaceholder")}
									value={currentPassword}
									onValueChange={setCurrentPassword}
									isRequired
								/>
								<PasswordInput
									autoComplete="new-password"
									label={t("auth.newPassword")}
									placeholder={t("passwordPlaceholder")}
									value={newPassword}
									onValueChange={setNewPassword}
									isRequired
									minLength={8}
								/>
								<Button
									type="submit"
									disabled={
										isPasswordPending ||
										!currentPassword ||
										newPassword.length < 8
									}
									variant="default"
								>
									{isPasswordPending && <Spinner data-icon="inline-start" />}
									{t("profile.changePassword")}
								</Button>
							</form>
						</CardContent>
					</Card>
				</div>

				<div className="mt-12 flex flex-wrap gap-3">
					<Button asChild variant="muted">
						<Link
							to="/$locale/$userId/projects"
							params={{ locale, userId: user.$id }}
						>
							{<Icon icon="lucide:arrow-left" className="size-5" />}
							{t("common.backToDashboard")}
						</Link>
					</Button>

					<Button onClick={signOut} variant="destructive-soft" type="button">
						{<Icon icon="lucide:log-out" className="size-5" />}
						{t("project.logout")}
					</Button>
					<AlertDialogTrigger asChild>
						<Button
							onClick={onDeleteModalOpen}
							variant="destructive-soft"
							type="button"
						>
							{<Icon icon="lucide:trash-2" className="size-5" />}
							{t("profile.deleteAccount")}
						</Button>
					</AlertDialogTrigger>
				</div>

				<AlertDialogContent
					onEscapeKeyDown={(event) => {
						if (isDeletePending) event.preventDefault();
					}}
					className="max-h-[85dvh] overflow-y-auto sm:max-w-lg"
				>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("profile.deleteAccount")}</AlertDialogTitle>
					</AlertDialogHeader>
					<div className="flex flex-col gap-4">
						<AlertDialogDescription className="text-sm text-foreground-600">
							{t("profile.deleteAccountDescription")}
						</AlertDialogDescription>
						<p className="text-sm font-medium text-danger">
							{t("profile.deleteAccountOwnershipNote")}
						</p>
						<Field>
							<FieldLabel htmlFor={`${fieldId}-102`}>
								{t("profile.deleteAccountConfirmationLabel")}
							</FieldLabel>
							<Input
								id={`${fieldId}-102`}
								autoFocus
								placeholder={t("profile.deleteAccountConfirmation")}
								value={deleteConfirmation}
								onChange={(event) =>
									setDeleteConfirmation(event.currentTarget.value)
								}
							/>
						</Field>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel asChild>
							<Button
								onClick={() => {
									setDeleteConfirmation("");
									onDeleteModalClose();
								}}
								disabled={isDeletePending}
								variant="muted"
								type="button"
							>
								{t("abort")}
							</Button>
						</AlertDialogCancel>
						<Button
							onClick={deleteAccount}
							disabled={
								deleteConfirmation !== t("profile.deleteAccountConfirmation") ||
								isDeletePending
							}
							variant="destructive"
							type="button"
						>
							{isDeletePending && <Spinner data-icon="inline-start" />}
							{t("profile.deleteAccount")}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</PageContainer>
		</AlertDialog>
	);
}
